import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import UserNotification from "util/UserNotification";
import {
  getInstalledBundles,
  getBundlesStatus,
  getLatestBundle,
  updateBundle,
  deleteBundle,
  uploadBundle,
  getHubBundles,
  installHubBundle,
  getReleaseNotes,
  getInstallationStatus,
  checkPreviousBundle
} from "illuminate/hooks/api/bundles";
import { onError, defaultOnError } from "util/conditional/onError";
export function useGetInstalledBundles() {
  var _a;
  const { data, isLoading } = useQuery({
    queryKey: ["all-bundles"],
    queryFn: () => onError(getInstalledBundles(), (error) => UserNotification.error(error.message))
  });
  return {
    bundles: data || [],
    bundleVersion: isLoading ? void 0 : (_a = data.find((bundle) => bundle.enabled)) == null ? void 0 : _a.version,
    loadingBundles: isLoading
  };
}
export function useGetBundlesStatus() {
  const { data, isLoading } = useQuery({
    queryKey: ["bundles-status"],
    queryFn: () => onError(getBundlesStatus(), (error) => UserNotification.error(error.message))
  });
  return {
    bundlesStatus: data || {},
    loadingBundlesStatus: isLoading
  };
}
export function useGetLatestBundle() {
  const { data, isInitialLoading } = useQuery({
    queryKey: ["check-for-new-bundle"],
    queryFn: () => onError(getLatestBundle(), (error) => UserNotification.error(error.message)),
    refetchInterval: 1e3 * 60 * 30
  });
  const { version, is_new_version } = data != null ? data : { version: "", is_new_version: false };
  return { version, isNew: is_new_version, isInitialLoading };
}
export function useUpdateBundle() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateBundle,
    onSuccess: () => {
      UserNotification.success("Bundle updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["check-for-new-bundle"]
      });
      queryClient.invalidateQueries({
        queryKey: ["bundles-status"]
      });
      queryClient.invalidateQueries({
        queryKey: ["all-bundles"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateBundle: mutateAsync,
    updatingBundle: isLoading
  };
}
export function useDeleteBundle() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteBundle,
    onSuccess: () => {
      UserNotification.success("Bundle deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["check-for-new-bundle"]
      });
      queryClient.invalidateQueries({
        queryKey: ["bundles-status"]
      });
      queryClient.invalidateQueries({
        queryKey: ["all-bundles"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteBundle: mutateAsync,
    deletingBundle: isLoading
  };
}
export function useUploadBundle() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: uploadBundle,
    onSuccess: () => {
      UserNotification.success("Bundle uploaded successfully");
      queryClient.invalidateQueries({
        queryKey: ["check-for-new-bundle"]
      });
      queryClient.invalidateQueries({
        queryKey: ["bundles-status"]
      });
      queryClient.invalidateQueries({
        queryKey: ["all-bundles"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    uploadBundle: (bundleFile, nextPercentage) => new Promise((resolve, reject) => {
      mutateAsync(
        { bundleFile, nextPercentage },
        {
          onSuccess: (version) => resolve(version),
          onError: (error) => reject(error)
        }
      );
    }),
    uploadingBundle: isLoading
  };
}
export function useGetHubBundles() {
  var _a;
  const { data, isLoading } = useQuery({
    queryKey: ["hub-bundles"],
    queryFn: () => onError(getHubBundles(), (error) => {
      UserNotification.error(error.message);
    })
  });
  return {
    hubBundles: (_a = data == null ? void 0 : data.bundles) != null ? _a : [],
    loadingHubBundles: isLoading
  };
}
export function useGetReleaseNotes(version) {
  var _a;
  const { data, isLoading } = useQuery({
    queryKey: ["release-notes", version],
    queryFn: () => onError(getReleaseNotes(version), (error) => UserNotification.error(error.message)),
    enabled: !!version,
    retry: 2
  });
  return {
    releaseNotes: (_a = data == null ? void 0 : data.release_notes.split(/\n.+\n={20,25}\n/)[0].trim()) != null ? _a : "#### No release notes available ...",
    loadingReleaseNotes: isLoading
  };
}
export function useInstallHubBundle() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: installHubBundle,
    onSuccess: () => {
      UserNotification.success("Bundle installed successfully");
      queryClient.invalidateQueries({
        queryKey: ["all-bundles"]
      });
      queryClient.invalidateQueries({
        queryKey: ["check-for-new-bundle"]
      });
      queryClient.invalidateQueries({
        queryKey: ["hub-bundles"]
      });
      queryClient.invalidateQueries({
        queryKey: ["illuminate-bundle-packs"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    installHubBundle: mutateAsync,
    installingHubBundle: isLoading
  };
}
export function useGetInstallationStatus(disabled = false) {
  const { data, isLoading } = useQuery({
    queryKey: ["install-status"],
    queryFn: () => onError(getInstallationStatus(), (error) => UserNotification.error(error.message)),
    enabled: !disabled,
    refetchInterval: 1e3,
    retry: 2
  });
  return {
    installationStatus: isLoading ? { install_in_progress: false, previous_install: null } : data || { install_in_progress: false, previous_install: null },
    gettingStatus: isLoading
  };
}
export function useCheckPreviousBundle() {
  const { data, isLoading } = useQuery({
    queryKey: ["check-previous"],
    queryFn: () => defaultOnError(checkPreviousBundle(), "Checking previous bundle failed"),
    retry: 2
  });
  return {
    previousBundle: data,
    checkingPreviousBundle: isLoading
  };
}
